import axios from 'axios';

const HEIC_MIME_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence'
]);

const HEIC_EXTENSION_REGEX = /\.(heic|heif)$/i;
const SHOULD_LOG_UPLOAD_DEBUG = process.env.REACT_APP_DEBUG_UPLOADS === 'true';

const toArray = (value) => (Array.isArray(value) ? value : [value]).filter(Boolean);

const isHeicFile = (file) => {
  const mimeType = (file?.type || '').toLowerCase();
  if (HEIC_MIME_TYPES.has(mimeType)) return true;
  return HEIC_EXTENSION_REGEX.test(file?.name || '');
};

const makeJpegName = (fileName = 'upload.heic') => fileName.replace(HEIC_EXTENSION_REGEX, '.jpg');

const convertViaServer = async (file) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (!token) return null;

  const apiBase = process.env.REACT_APP_API_URL || '/api';
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await axios.post(`${apiBase}/upload/convert-image`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      },
      responseType: 'arraybuffer'
    });

    const contentType = response.headers?.['content-type'] || 'image/jpeg';
    const headerName = response.headers?.['x-converted-filename'];
    const decodedName = headerName ? decodeURIComponent(headerName) : makeJpegName(file.name);
    const blob = new Blob([response.data], { type: contentType });
    return new File([blob], decodedName, {
      type: contentType,
      lastModified: Date.now()
    });
  } catch (error) {
    if (SHOULD_LOG_UPLOAD_DEBUG) {
      console.warn('Server-side HEIC conversion failed for file:', file?.name, error);
    }
    return null;
  }
};

export const normalizeImageUploads = async (files = []) => {
  if (!Array.isArray(files) || files.length === 0) {
    return { files: [], convertedCount: 0, skippedFiles: [], failedConversions: [] };
  }

  let heic2any = null;
  let convertedCount = 0;
  const skippedFiles = [];
  const failedConversions = [];

  const normalized = await Promise.all(files.map(async (file) => {
    if (!isHeicFile(file)) return file;

    try {
      if (!heic2any) {
        const module = await import('heic2any');
        heic2any = module?.default || module;
      }

      const converted = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.9
      });

      const jpegBlob = toArray(converted)[0];
      if (!jpegBlob) {
        throw new Error('No JPEG blob returned from converter');
      }

      convertedCount += 1;
      const convertedFileName = makeJpegName(file.name);
      return new File([jpegBlob], convertedFileName, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
    } catch (error) {
      if (SHOULD_LOG_UPLOAD_DEBUG) {
        console.warn('HEIC conversion unavailable in browser for file:', file?.name, error);
      }
      const serverConverted = await convertViaServer(file);
      if (serverConverted) {
        convertedCount += 1;
        return serverConverted;
      }
      failedConversions.push(file?.name || 'unknown-file');
      return file;
    }
  }));

  return {
    files: normalized.filter(Boolean),
    convertedCount,
    skippedFiles,
    failedConversions
  };
};

